// Importing the custom API client instance
import apiClient from "../apiClient";
import CourseControllers from "@/Controllers/CoursesControllers";
/**
 * Fetch courses with filters and pagination
 *
 * This service fetches courses based on dynamic filters such as category, teacher, page, and items per page.
 *
 * @param {Object} params - The parameters for the API request.
 * @param {Array<number>} params.category_id - Array of category IDs to filter.
 * @param {Array<number>} params.teacher_id - Array of teacher IDs to filter.
 * @param {number} params.page - The page number for pagination.
 * @param {number} params.per_page - The number of items per page.
 * @returns {Promise<Object>} - The API response data.
 * @throws {Error} - Throws an error if the API request fails.
 */
export const fetchCourses = async ({
  category_id = [],
  teacher_id = [],
  page = 1,
  per_page = 10,
  query = "",
}) => {
  try {
    // Construct the query parameters dynamically
    const params = new URLSearchParams();
    category_id.forEach((id) => params.append("category_id[]", id));
    teacher_id.forEach((id) => params.append("teacher_id[]", id));
    params.append("page", page);
    params.append("per_page", per_page);

    // Add the search parameter if it exists
    if (query) {
      params.append("query", query);
    }

    // Make the API request
    const response = await apiClient.get(
      `${CourseControllers.AllCoursesData}?${params.toString()}`
    );
    // Return the response data
    return response.data;
  } catch (error) {
    throw new Error("Failed to fetch courses. Please try again later.");
  }
};

/**
 * Service function to fetch the latest courses
 * @returns {Promise<void>} - Logs the response or error in the console
 */
export const getLatestCourses = async () => {
  try {
    const response = await apiClient.get(
      `${CourseControllers.LatestCoursesMenu}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      "Failed to fetch the latest courses. Please try again later."
    );
  }
};

/**
 * Fetches details of a single course.
 *
 * This function makes a GET request to fetch a specific course by its slug.
 * Optionally, it supports including a coupon code as a query parameter.
 *
 * @param {string} courseSlug - The slug of the course to fetch.
 * @param {string} [coupon] - (Optional) A coupon code to apply.
 * @returns {Promise<Object>} - Returns the data from the API response if successful.
 * @throws {Error} - Throws an error with the message if the request fails.
 */
export const getSingleCourse = async (courseSlug, coupon) => {
  try {
    const response = await apiClient.get(
      `${CourseControllers.AllCoursesData}/${courseSlug}`,
      {
        params: {
          coupon: coupon || undefined, // Include the coupon if provided
        },
      }
    );
    return response.data;
  } catch (error) {
   // alert(error.message);
    throw new Error("Failed to fetch course details.");
  }
};

/**
 * Fetches the user's course notifications.
 *
 * @returns {Promise<Object>} A promise that resolves to the course notifications data.
 * @throws {Error} Throws an error if the request fails.
 */
export const coursesNotifications = async () => {
  try {
    const response = await apiClient.get(`${CourseControllers.notification}`);
   // console.log(response)
    return response.data;
  } catch (error) {
    throw new Error("Failed to fetch course Notifications.");
  }
};

/**
 * Get Course Demo
 */
export const getCourseDemo = async (id) => {
  try{
    const response = await apiClient.get(`${CourseControllers.courseDemo}/${id}`);
    return response.data;
  }catch(error){
    console.log(error)
  }
}
/***
 * Featured coursee service
 *
 * @returns {Promise<Object>} A promise that resolves to the data of featured courses.
 * @throws Will alert the user if the request fails.
 *
 */
export const FeaturedCourses = async () => {
  try {
    const response = await apiClient.get(
      `${CourseControllers.featuredCourses}`
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

// get Course reviews
export const courseReviews = async (courseSlug, page = 1) => {
  try {
    const response = await apiClient.get(
      `/courses/${courseSlug}/reviews?page=${page}`
    );
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching course reviews:", error);
    throw error; // Re-throw the error to handle it in the calling function
  }
};

//Add update review
export const addUpdateReview = async (courseSlug, reviewdata) => {
  try {
    const response = await apiClient.post(
      `/courses/${courseSlug}/reviews`,
      reviewdata
    );
    return response.data.data; // Return the response data
  } catch (error) {
    if (error.response && error.response.data) {
      const serverMessage = error.response.data.message || "An error occurred";
      throw new Error(serverMessage); // Throw the error message
    } else {
      // Handle unexpected errors (like network issues)
      throw new Error("An unexpected error occurred. Please try again later.");
    }
  }
};
